import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Save, RotateCcw, Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
});

export const AdminAbout = () => {
  const [content, setContent] = useState({
    tagline: '',
    title: '',
    paragraphs: [],
    credits: [],
    image_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/content/about`);
      setContent(response.data);
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/about`, { ...content, id: 'about' }, getAuthHeaders());
      toast.success('About section saved!');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handleParagraphChange = (index, value) => {
    const newParagraphs = [...content.paragraphs];
    newParagraphs[index] = value;
    setContent(prev => ({ ...prev, paragraphs: newParagraphs }));
  };

  const addParagraph = () => {
    setContent(prev => ({ ...prev, paragraphs: [...prev.paragraphs, ''] }));
  };

  const removeParagraph = (index) => {
    setContent(prev => ({ ...prev, paragraphs: prev.paragraphs.filter((_, i) => i !== index) }));
  };

  const handleCreditChange = (index, value) => {
    const newCredits = [...content.credits];
    newCredits[index] = value;
    setContent(prev => ({ ...prev, credits: newCredits }));
  };

  const addCredit = () => {
    setContent(prev => ({ ...prev, credits: [...prev.credits, ''] }));
  };

  const removeCredit = (index) => {
    setContent(prev => ({ ...prev, credits: prev.credits.filter((_, i) => i !== index) }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="admin-about">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">About Section</h1>
          <p className="text-muted-foreground">Edit Kerry's biography and credentials</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchContent} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-about-btn">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-lg border border-border p-6 space-y-4">
          <h2 className="font-semibold">Basic Info</h2>
          <div>
            <label className="block text-sm font-medium mb-2">Tagline</label>
            <input
              type="text"
              value={content.tagline}
              onChange={(e) => handleChange('tagline', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Section Title</label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Image URL</label>
            <input
              type="url"
              value={content.image_url}
              onChange={(e) => handleChange('image_url', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none"
              placeholder="https://..."
            />
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Biography Paragraphs</h2>
            <Button variant="outline" size="sm" onClick={addParagraph}>
              <Plus className="w-4 h-4 mr-1" /> Add Paragraph
            </Button>
          </div>
          <div className="space-y-4">
            {content.paragraphs.map((para, index) => (
              <div key={index} className="flex gap-2">
                <textarea
                  value={para}
                  onChange={(e) => handleParagraphChange(index, e.target.value)}
                  rows={3}
                  className="flex-1 px-4 py-3 border border-border rounded-lg focus:border-primary focus:outline-none resize-none"
                  placeholder={`Paragraph ${index + 1}...`}
                />
                <Button variant="ghost" size="icon" onClick={() => removeParagraph(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Selected Credits</h2>
            <Button variant="outline" size="sm" onClick={addCredit}>
              <Plus className="w-4 h-4 mr-1" /> Add Credit
            </Button>
          </div>
          <div className="space-y-2">
            {content.credits.map((credit, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={credit}
                  onChange={(e) => handleCreditChange(index, e.target.value)}
                  className="flex-1 px-4 py-2 border border-border rounded-lg focus:border-primary focus:outline-none"
                  placeholder="e.g., The Sound of Music - London Palladium"
                />
                <Button variant="ghost" size="icon" onClick={() => removeCredit(index)} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAbout;
